import { useState, useRef, useEffect, useCallback } from "react"
import type { PlotlyDataLayoutConfig } from "plotly.js-dist-min"
import { useAnalytics } from "connected-components/analytics"
import type { ArtifactKind } from "connected-components/dashboard/modules/ai-artifact/types"
import { dashboardEvents, DASHBOARD_EVENTS } from "helpers/events/dashboard-events"
import { warn } from "helpers/dev-logger"
import { AI_TEAMMATE_EVENTS } from "../analytics/events"

const PIN_TIMEOUT_MS = 10000
const FEEDBACK_DISPLAY_MS = 2000

/**
 * AI Teammate artifact types (use 'id', not 'artifact_id')
 */
interface BaseArtifact {
  id: string
  title: string
}

interface PlotArtifact extends BaseArtifact {
  figure: PlotlyDataLayoutConfig & Record<string, unknown>
  tableArtifactId?: string
}

interface TableArtifact extends BaseArtifact {
  records: Record<string, unknown>[]
  columns: {
    label: string
    format: string
    id?: string
    key?: string
    field?: string
    name?: string
  }[]
  explainSteps?: string
}

export type AiTeammateArtifact = PlotArtifact | TableArtifact

interface UsePinArtifactOptions {
  artifact: AiTeammateArtifact
  kind: ArtifactKind
  title?: string
}

type PinState = "idle" | "pinning" | "success" | "error"

/**
 * Hook for pinning artifacts to dashboard
 *
 * Handles event-based communication with Dashboard using a request-response pattern
 * with automatic timeout and cleanup on unmount.
 */
export function usePinArtifact({
  artifact,
  kind,
  title,
}: UsePinArtifactOptions) {
  const [pinState, setPinState] = useState<PinState>("idle")
  const analytics = useAnalytics()
  const cleanupRef = useRef<(() => void)[]>([])

  const cleanup = useCallback(() => {
    cleanupRef.current.forEach((fn) => fn())
    cleanupRef.current = []
  }, [])

  useEffect(() => cleanup, [cleanup])

  const pin = useCallback(() => {
    if (pinState !== "idle") return

    cleanup()
    setPinState("pinning")

    analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_PIN_INITIATED, {
      kind,
      artifactId: artifact.id,
    })

    dashboardEvents.emit(DASHBOARD_EVENTS.ARTIFACT_PIN_REQUESTED, {
      artifact,
      kind,
      title,
    })

    const handleSuccess = (payload: {
      artifactId: string
      moduleId: string
      pinnedId: string
      kind: string
    }) => {
      if (payload.artifactId !== artifact.id) return

      cleanup()
      setPinState("success")

      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_PIN_SUCCESS, {
        kind,
        artifactId: artifact.id,
      })

      const successTimer = window.setTimeout(() => {
        setPinState("idle")
      }, FEEDBACK_DISPLAY_MS)

      cleanupRef.current.push(() => clearTimeout(successTimer))
    }

    const handleFailure = (payload: {
      artifactId: string
      error: string
      moduleId?: string
      kind: string
    }) => {
      if (payload.artifactId !== artifact.id) return

      cleanup()
      setPinState("error")

      warn("Failed to pin artifact:", payload.error)
      analytics.track(AI_TEAMMATE_EVENTS.ARTIFACT_PIN_FAILED, {
        kind,
        artifactId: artifact.id,
        error: payload.error,
      })

      const errorTimer = window.setTimeout(() => {
        setPinState("idle")
      }, FEEDBACK_DISPLAY_MS)

      cleanupRef.current.push(() => clearTimeout(errorTimer))
    }

    const handleTimeout = () => {
      cleanup()
      setPinState("error")
      warn("Pin request timeout - Dashboard may not be mounted")

      const timeoutReset = window.setTimeout(() => {
        setPinState("idle")
      }, FEEDBACK_DISPLAY_MS)

      cleanupRef.current.push(() => clearTimeout(timeoutReset))
    }

    const removeSuccessListener = dashboardEvents.on(
      DASHBOARD_EVENTS.ARTIFACT_PINNED,
      handleSuccess,
    )
    const removeFailureListener = dashboardEvents.on(
      DASHBOARD_EVENTS.ARTIFACT_PIN_FAILED,
      handleFailure,
    )

    const timeoutId = window.setTimeout(handleTimeout, PIN_TIMEOUT_MS)

    cleanupRef.current.push(
      removeSuccessListener,
      removeFailureListener,
      () => clearTimeout(timeoutId),
    )
  }, [pinState, artifact, kind, title, analytics, cleanup])

  return {
    pin,
    isPinning: pinState === "pinning",
    isPinned: pinState === "success",
    hasError: pinState === "error",
    pinState,
  }
}
